import React, { Suspense } from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import homeConfig from "../../assets/configs/homeConfig";
import './Home.css';
import Review from '../../components/Review';
import styled from 'styled-components';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import SEO from '../../components/SEO/SEO';

// Update the lazy loading with error boundaries
const Timeline = React.lazy(() => import('../../components/Timeline/Timeline'));
const Header = React.lazy(() => import('../../components/Header'));
const Projects = React.lazy(() => import('../../components/Projects'));

// Add error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <h3>Something went wrong.</h3>
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

// Common animation variants
const fadeInUpVariant = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
};

// Add the missing SectionContainer component
const SectionContainer = styled.section`
    padding: 4rem 0;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 768px) {
        padding: 3rem 1rem;
    }
`;

const Home = () => {
    return (
        <>
            <SEO 
                title="Xtreme Roofing & Construction | Professional Roofing Services"
                description="Expert roofing and construction services with over 20 years of experience. Get professional installation, repairs, and 24/7 emergency service."
            />
            <ErrorBoundary>
                <section className="main-content">
                    <Helmet>
                        <title>Xtreme Roofing & Construction | Professional Roofing Services</title>
                        <meta name="description" content="Expert roofing and construction services with over 20 years of experience. Get professional installation, repairs, and 24/7 emergency service. Licensed and insured." />
                        <meta name="keywords" content="roofing, construction, roof repair, emergency roofing, commercial roofing, residential roofing, contractors" />
                        
                        {/* OpenGraph Tags */}
                        <meta property="og:title" content="Paisalocal - Transform Your Home with Trusted Professionals" />
                        <meta property="og:description" content="Connect with verified contractors, get instant estimates, and ensure secure payments. Transform your home with Paisalocal's trusted network of professionals." />
                        <meta property="og:type" content="website" />
                        <meta property="og:image" content="/og-image.jpg" />
                        <meta property="og:site_name" content="Paisalocal" />
                        <meta property="og:locale" content="en_US" />
                        <meta property="og:locale:alternate" content="es_US" />

                        {/* Twitter Card Tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:site" content="@paisalocal" />
                        <meta name="twitter:title" content="Paisalocal - Home Improvement Made Easy" />
                        <meta name="twitter:description" content="Find verified contractors, get instant estimates, and transform your space with expert craftsmanship." />
                        <meta name="twitter:image" content="/twitter-card.jpg" />

                        {/* Additional SEO Tags */}
                        <meta name="robots" content="index, follow, max-image-preview:large" />
                        <meta name="googlebot" content="index, follow" />
                        <meta name="theme-color" content="#FF6B00" />
                        <meta name="apple-mobile-web-app-capable" content="yes" />
                        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                        <link rel="canonical" href="https://paisalocal.com" />
                        <link rel="alternate" href="https://paisalocal.com/es" hreflang="es-us" />
                        <link rel="alternate" href="https://paisalocal.com" hreflang="en-us" />

                        {/* Schema.org Markup */}
                        <script type="application/ld+json">
                            {JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "Organization",
                                "name": "Paisalocal",
                                "url": "https://paisalocal.com",
                                "logo": "https://paisalocal.com/logo.png",
                                "description": "Find and hire verified contractors for your home improvement projects. Get instant estimates, secure payments, and quality assurance with Paisalocal.",
                                "aggregateRating": {
                                    "@type": "AggregateRating",
                                    "ratingValue": "4.8",
                                    "reviewCount": "5000",
                                    "bestRating": "5",
                                    "worstRating": "1"
                                },
                                "sameAs": [
                                    "https://www.facebook.com/paisalocal",
                                    "https://twitter.com/paisalocal",
                                    "https://www.linkedin.com/company/paisalocal",
                                    "https://www.instagram.com/paisalocal"
                                ],
                                "contactPoint": {
                                    "@type": "ContactPoint",
                                    "telephone": "+1-800-PAISA",
                                    "contactType": "customer service",
                                    "availableLanguage": ["English", "Spanish"]
                                }
                            })}
                        </script>
                        <script type="application/ld+json">
                            {JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "Service",
                                "name": "Contractor Matching Service",
                                "provider": {
                                    "@type": "Organization",
                                    "name": "Paisalocal"
                                },
                                "serviceType": "Home Improvement",
                                "areaServed": {
                                    "@type": "Country",
                                    "name": "United States"
                                },
                                "description": "Connect with verified contractors for home improvement projects",
                                "offers": {
                                    "@type": "Offer",
                                    "availability": "https://schema.org/InStock",
                                    "price": "0",
                                    "priceCurrency": "USD"
                                },
                                "potentialAction": {
                                    "@type": "SearchAction",
                                    "target": {
                                        "@type": "EntryPoint",
                                        "urlTemplate": "https://paisalocal.com/calculator?project={project_type}"
                                    },
                                    "query-input": "required name=project_type"
                                }
                            })}
                        </script>
                    </Helmet>

                    <Container fluid className="home-content" id="home">
                        <Suspense fallback={<LoadingSpinner />}>
                            <Header />
                        </Suspense>
                        
                        <AnimatePresence>
                            
                            <SectionContainer>
                                <Review />
                            </SectionContainer>

                            <SectionContainer>
                                <Suspense fallback={<LoadingSpinner />}>
                                    <Projects />
                                </Suspense>
                            </SectionContainer>

                            <SectionContainer>
                                <Suspense fallback={<LoadingSpinner />}>
                                    <Timeline items={homeConfig.workTimeline} />
                                </Suspense>
                            </SectionContainer>

                            <HeroSection {...fadeInUpVariant}>
                                <BackgroundAccents />
                                <HeroContent>
                                    <HeroTitle>
                                        Elevate Your Home's Potential
                                        <span>with Xtreme Roofing & Construction</span>
                                    </HeroTitle>
                                    <HeroSubtitle>
                                        From premium roofing solutions to stunning outdoor living spaces, 
                                        we're your trusted partner in home transformation. Specializing in 
                                        expert roofing, custom remodels, and luxurious outdoor living areas 
                                        that bring your vision to life.
                                    </HeroSubtitle>
                                    <ServiceHighlights>
                                        <ServiceTag>Premium Roofing</ServiceTag>
                                        <ServiceTag>Custom Remodels</ServiceTag>
                                        <ServiceTag>Outdoor Living</ServiceTag>
                                    </ServiceHighlights>
                                    <HeroButton
                                        to="/calculator"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        Get Your Estimate <span>→</span>
                                    </HeroButton>
                                </HeroContent>
                            </HeroSection>


                            <TrustSignalsSection>
                                <Container>
                                    <CertificationsSection>
                                        {homeConfig.certifications.map((cert, index) => (
                                            <CertBadge key={index}>
                                                <IconWrapper>{cert.icon}</IconWrapper>
                                                <span>{cert.title}</span>
                                                <Description>{cert.description}</Description>
                                            </CertBadge>
                                        ))}
                                    </CertificationsSection>
                                </Container>
                            </TrustSignalsSection>
                        </AnimatePresence>
                    </Container>
                </section>
            </ErrorBoundary>
        </>
    );
};

const HeroSection = styled(motion.div)`
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 4rem 2rem;
  overflow: hidden;
  background: linear-gradient(
    145deg,
    #1A1A1A 0%,
    #2C2C2C 100%
  );

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(255, 107, 0, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(255, 107, 0, 0.08) 0%, transparent 70%);
    z-index: -1;
  }
`;

const BackgroundAccents = styled(motion.div)`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 107, 0, 0.05) 0%,
    rgba(255, 107, 0, 0.08) 100%
  );
  filter: blur(60px);
  z-index: 0;
`;

const HeroContent = styled(motion.div)`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 900;
  color: #FFFFFF;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;

  span {
    display: block;
    background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: clamp(2.5rem, 5vw, 4rem);
    margin-top: 0.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 2vw, 1.4rem);
  color: rgba(255, 255, 255, 0.9);
  max-width: 650px;
  margin: 0 auto 3.5rem;
  line-height: 1.6;
  letter-spacing: -0.01em;
`;

const HeroButton = styled(motion(Link))`
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  color: white;
  text-decoration: none;
  padding: 1.4rem 3.2rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 8px 30px rgba(255, 107, 0, 0.3),
    0 2px 4px rgba(255, 107, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 12px 40px rgba(255, 107, 0, 0.4),
      0 4px 8px rgba(255, 107, 0, 0.3);
  }
`;

const TrustSignalsSection = styled.section`
  padding: 6rem 0;
  background: var(--background-dark);
  position: relative;
`;

const CertificationsSection = styled(motion.div)`
  text-align: center;
  margin-top: 6rem;
  position: relative;
  z-index: 1;
`;

const CertBadge = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    padding: 1.5rem;
    border-radius: 16px;
    background: rgba(44, 44, 44, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 107, 0, 0.1);
    transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);

    img {
        width: 90px;
        height: 90px;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        filter: drop-shadow(0 4px 12px rgba(255, 107, 0, 0.1));
    }

    &:hover {
        background: rgba(44, 44, 44, 0.95);
        transform: translateY(-5px);
        box-shadow: 
            0 20px 40px rgba(255, 107, 0, 0.1),
            0 4px 12px rgba(255, 107, 0, 0.06);
    }

    &:hover img {
        transform: scale(1.1) rotate(8deg);
        filter: drop-shadow(0 8px 20px rgba(255, 107, 0, 0.2));
    }

    span {
        color: #FFFFFF;
        font-size: 1rem;
        font-weight: 500;
        transition: color 0.3s ease;
    }

    &:hover span {
        color: #FF6B00;
    }
`;

const IconWrapper = styled.div`
    font-size: 2rem;
    color: #FF6B00;
    margin-bottom: 1rem;
`;

const Description = styled.p`
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin-top: 0.5rem;
`;

// Add new styled components for error handling
const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    color: #FFFFFF;
`;

const RetryButton = styled.button`
    background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
    color: white;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 1rem;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(255, 107, 0, 0.15);
    }
`;

const ServiceHighlights = styled.div`
    display: flex;
    gap: 1rem;
    margin: 2rem 0;
    flex-wrap: wrap;
    justify-content: center;

    @media (max-width: 768px) {
        gap: 0.5rem;
    }
`;

const ServiceTag = styled.span`
    background: rgba(255, 87, 34, 0.1);
    color: #FF5722;
    padding: 0.5rem 1.5rem;
    border-radius: 50px;
    font-weight: 500;
    font-size: 1.1rem;
    border: 2px solid rgba(255, 87, 34, 0.2);
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 87, 34, 0.15);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 0.4rem 1rem;
    }
`;

export default Home;